// components/FloatingButtons.js
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, MessageSquare, X, ChevronUp } from 'lucide-react';

const FloatingButtons = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  const handleCall = () => {
    window.location.href = 'tel:+923043309005';
  };

  const handleWhatsApp = () => {
    // International format without spaces and special characters
    const phoneNumber = '3390169005';
    const message = encodeURIComponent('Hello! I would like to discuss a project with Raza Tech Solutions.');
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
  };

  const handleClose = () => {
    setIsVisible(false);
    // Store in localStorage to remember user's preference
    localStorage.setItem('floatingButtonsHidden', 'true');
  };

  // Check if user previously hid the buttons
  React.useEffect(() => {
    const hidden = localStorage.getItem('floatingButtonsHidden');
    if (hidden === 'true') {
      setIsVisible(false);
    }
  }, []);

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            className="mb-4 space-y-3"
          >
            {/* WhatsApp Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleWhatsApp}
              className="flex items-center justify-between bg-[#25D366] text-white px-5 py-3 rounded-xl shadow-lg hover:shadow-xl transition-shadow w-48"
            >
              <div className="flex items-center">
                <MessageSquare className="w-5 h-5 mr-3" />
                <div className="text-left">
                  <div className="font-semibold">WhatsApp</div>
                  <div className="text-xs opacity-90">Instant Response</div>
                </div>
              </div>
              <div className="text-xs bg-white/20 px-2 py-1 rounded">
                24/7
              </div>
            </motion.button>

            {/* Call Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleCall}
              className="flex items-center justify-between bg-blue-600 text-white px-5 py-3 rounded-xl shadow-lg hover:shadow-xl transition-shadow w-48"
            >
              <div className="flex items-center">
                <Phone className="w-5 h-5 mr-3" />
                <div className="text-left">
                  <div className="font-semibold">Call Now</div>
                  <div className="text-xs opacity-90">+92 (304) 330-9005</div>
                </div>
              </div>
              <div className="text-xs bg-white/20 px-2 py-1 rounded">
                9AM-6PM
              </div>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Floating Button */}
      <div className="relative">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsExpanded(!isExpanded)}
          className={`flex items-center justify-center w-14 h-14 rounded-full shadow-2xl transition-all ${
            isExpanded ? 'bg-gray-800' : 'bg-gradient-to-r from-blue-600 to-purple-600'
          }`}
        >
          <AnimatePresence mode="wait">
            {isExpanded ? (
              <motion.div
                key="close"
                initial={{ rotate: -180, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 180, opacity: 0 }}
              >
                <X className="w-6 h-6 text-white" />
              </motion.div>
            ) : (
              <motion.div
                key="chat"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.5, opacity: 0 }}
                className="relative"
              >
                <MessageSquare className="w-6 h-6 text-white" />
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white"></div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.button>

        {/* Close Button */}
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={handleClose}
          className="absolute -top-2 -left-2 w-6 h-6 bg-gray-700 text-white rounded-full flex items-center justify-center text-xs shadow-lg hover:bg-gray-800 transition"
          title="Hide buttons"
        >
          <X className="w-3 h-3" />
        </motion.button>

        {/* Badge */}
        {!isExpanded && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1"
          >
            <div className="relative">
              <div className="animate-ping absolute inset-0 bg-green-500 rounded-full opacity-75"></div>
              <div className="relative bg-green-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                2
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Chat Indicator */}
      {!isExpanded && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="absolute -top-12 right-0 bg-gray-900 text-white text-sm px-3 py-2 rounded-lg whitespace-nowrap"
        >
          <div className="flex items-center">
            <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
            We're online
          </div>
          <div className="absolute bottom-0 right-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900"></div>
        </motion.div>
      )}
    </div>
  );
};

export default FloatingButtons;